<template>
  <section>
    <v-row>
      <v-col class="p-0">
        <VSelect
          v-model="selectedPlatform"
          :label="$t('forms.labels.platform')"
          itemValue="key"
          :items="displayedPlatform"
          outlined
        />
      </v-col>
      <v-col class="p-0 pl-3">
        <VSelect
          v-model="selectedLang"
          :label="$t('forms.labels.lang')"
          itemValue="key"
          :items="displayedLangs"
          outlined
        />
      </v-col>
    </v-row>
    <v-row>
      <VTextField
        id="starting_address"
        v-model="release[selectedPlatform].title[selectedLang]"
        :label="$t('forms.labels.title')"
        placeholder=""
        outlined
        dataCy="releaseMainTitle"
      />
    </v-row>
    <v-row>
      <p>{{ $t('forms.labels.releaseNote') }}</p>
      <GlobalComment
        position="opening"
        :comment="{message: release[selectedPlatform].note.message[selectedLang]}"
        :showAddSentences="false"
        :canUploadImages="false"
        :isLab="true"
        :showSaveBtn="false"
        editorDataCy="releaseMainNote"
        @commentChange="release[selectedPlatform].note.message[selectedLang] = $event"
      />
    </v-row>
    <v-row class="actions">
      <VCheckbox
        v-model="release[selectedPlatform].displayInNextLogin"
        outlined
        :label="$t('advancedConfiguration.displayInNextLogin')"
      />
      <v-btn
        color="primary"
        @click="startBroadcast"
      >
        {{ $t('administration.broadcast') }}
      </v-btn>
    </v-row>
    <v-row justify="end">
      <v-btn
        color="secondary"
        :disabled="!isDisabled"
        @click="onSave"
      >
        {{ $t('generic.save') }}
      </v-btn>
    </v-row>
  </section>
</template>

<script>
import GlobalComment from '@/views/laboratory/workOrder/summary/GlobalComment';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import { LOCALES } from '@/locales/i18n';
import { PLATFORMS } from '@/constants/platforms';
import { isEmpty } from 'lodash';
// eslint-disable-next-line import/no-cycle
import { getSocketClient } from '@/utilities/socket';
import moment from 'moment/moment';

export default {
    name: 'Release',
    components: {
        GlobalComment,
        VCheckbox,
        VSelect,
    },
    data() {
        return {
            release: {},
            selectedLang: 'en',
            selectedPlatform: 'web',
            hasEmit: false,
        };
    },
    computed: {
        isDisabled() {
            return !!(this.release?.[this.selectedPlatform].note.message?.[this.selectedLang]?.length
              && this.release?.[this.selectedPlatform].title?.[this.selectedLang]?.length);
        },
        displayedLangs() {
            return LOCALES.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
            }));
        },
        displayedPlatform() {
            return PLATFORMS.map(platform => ({
                name: this.$t(`platforms.${platform}`),
                key: platform,
            }));
        },
    },
    created() {
        // init object
        PLATFORMS.forEach(platform => {
            this.release = {
                ...this.release,
                [platform]: {
                    note: {},
                    title: {},
                    displayInNextLogin: false,
                    id: null,
                },
            };
            LOCALES.forEach(lang => {
                this.release[platform] = {
                    ...this.release[platform],
                    note: {
                        message: { ...this.release[platform].note.message, [lang]: '' },
                    },
                    title: { ...this.release[platform].title, [lang]: '' },
                };
            });
        });

        this.getData();
    },
    methods: {
        async getData() {
            const { data } = await this.$rq.getRelease();
            if (!isEmpty(data)) {
                PLATFORMS.forEach(platform => {
                    LOCALES.forEach(lang => {
                        this.release[platform] = {
                            ...this.release[platform],
                            note: {
                                message: data[platform]?.note?.message || { [lang]: '' },
                            },
                            title: data[platform]?.title || { [lang]: '' },
                            displayInNextLogin: data[platform]?.displayInNextLogin || false,
                            id: data[platform]?.id || null,
                        };
                    });
                });
            }
        },
        async onSave() {
            const params = [];
            Object.keys(this.release).forEach(platform => {
                params.push({
                    ...this.release[platform],
                    platform,
                });
            });
            const { data } = await this.$rq.updateRelease(params);
            if (data?.[this.selectedPlatform].id) {
                this.$toasted.global.toast_success({
                    message: this.$t('graphics.series.msgUpdateSuccess'),
                });
            }
            this.getData();
        },
        startBroadcast() {
            if (!this.hasEmit) {
                const socketClient = getSocketClient();
                socketClient.emit('AlertMaintenance', {
                    time: moment().utc().add(1, 'hour').unix(),
                }, hasEmit => {
                    this.hasEmit = hasEmit;
                    setTimeout(() => {
                        this.hasEmit = false;
                        // lock the broadcast button for 10s
                    }, 10000);
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.actions{
    display: flex;
    align-items: center;
    gap: 1rem;
}
</style>
